/**
 * Product bundle options web component.
 * Manages bundle variant visibility, quantity selectors, and sale price updates.
 */
import { formatMoney } from "customUtils";

class ProductBundleOptions extends HTMLElement {
  constructor() {
    super();
    this._variantChangeHandler = this._variantChangeHandler.bind(this);
  }

  connectedCallback() {
    this._initBundleOptions();
    document.addEventListener("variant:change", this._variantChangeHandler);
  }

  disconnectedCallback() {
    document.removeEventListener("variant:change", this._variantChangeHandler);
  }

  _updateSalePrices(totalPrice, accessoriesAdded) {
    const currencyFormat = window.themeVariables?.settings?.currencyCodeEnabled
      ? window.themeVariables.settings.moneyWithCurrencyFormat
      : window.themeVariables.settings.moneyFormat;
    const salePrices = document.querySelectorAll("sale-price[form]");

    salePrices.forEach((sp) => {
      sp.textContent = formatMoney(totalPrice, currencyFormat);

      if (accessoriesAdded) {
        sp.classList.remove("text-subdued");
        sp.classList.add("text-thicc");
      } else {
        sp.classList.add("text-subdued");
        sp.classList.remove("text-thicc");
      }
    });
  }

  _initBundleOptionsForVariant(parentElement) {
    const bundleProducts = parentElement.querySelectorAll("[data-bundle-product]");
    if (bundleProducts.length === 0) return;

    const updatePrices = () => {
      const selectedBundleProducts = [...bundleProducts].filter((product) => {
        const input = product.querySelector("[data-bulk-add]");
        return input.checked || input.dataset?.quantity;
      });

      const getBundleItemPrice = (product) => {
        const input = product.querySelector("[data-bulk-add]");
        return +product.dataset.price * +(input.dataset.quantity || 0);
      };

      const accessoryPrices = selectedBundleProducts
        .map((product) => getBundleItemPrice(product))
        .reduce((a, b) => a + b, 0);
      const originalprice = +(parentElement.dataset.originalPrice || 0);
      const totalPrice = originalprice + accessoryPrices;

      this._updateSalePrices(totalPrice, accessoryPrices > 0);
    };

    bundleProducts.forEach((product) => {
      const quantityInput = product.querySelector("[data-bulk-add]");

      if (quantityInput.type === "checkbox") {
        product.addEventListener("click", (event) => {
          event.stopPropagation();
          event.preventDefault();

          quantityInput.checked = !quantityInput.checked;
          quantityInput.checked ? (quantityInput.dataset.quantity = 1) : delete quantityInput.dataset.quantity;

          updatePrices();
        });
      } else {
        const quantityParent = product.querySelector(".product_bundle__product__input");
        const inventoryQuantity = +(quantityParent?.dataset.inventoryQuantity || 99);
        const plusBtn = product.querySelector("[data-bundle-item-quantity-plus]");
        const minusBtn = product.querySelector("[data-bundle-item-quantity-minus]");
        const currentQuantityElement = product.querySelector("[data-bundle-item-quantity]");

        const update = (newQuantity) => {
          if (currentQuantityElement) {
            currentQuantityElement.textContent = newQuantity;
          }
          newQuantity === 0 ? delete quantityInput.dataset.quantity : (quantityInput.dataset.quantity = newQuantity);
          updatePrices();
        };

        plusBtn?.addEventListener("click", (event) => {
          event.stopPropagation();
          event.preventDefault();

          const currentQuantity = +(currentQuantityElement?.textContent || 0);
          const newQuantity = currentQuantity + 1 > inventoryQuantity ? inventoryQuantity : currentQuantity + 1;
          update(newQuantity);
        });

        minusBtn?.addEventListener("click", (event) => {
          event.stopPropagation();
          event.preventDefault();

          const currentQuantity = +(currentQuantityElement?.textContent || 0);
          const newQuantity = currentQuantity - 1 <= 0 ? 0 : currentQuantity - 1;
          update(newQuantity);
        });
      }
    });
  }

  _initBundleOptions() {
    const variantBundles = this.querySelectorAll("[data-bundle-variant]");
    if (variantBundles.length === 0) return;

    variantBundles.forEach((bundle) => this._initBundleOptionsForVariant(bundle));
  }

  _variantChangeHandler(event) {
    const selectedVariantId = event.detail.variant.id;
    const variantBundles = this.querySelectorAll("[data-bundle-variant]");

    const matchingBundle = [...variantBundles].find((b) => +b.dataset.bundleVariant === selectedVariantId);
    if (matchingBundle) {
      matchingBundle.dataset.originalPrice = event.detail.variant.price;
    }

    this._updateSalePrices(event.detail.variant.price, false);

    variantBundles.forEach((bundle) => {
      bundle.setAttribute("hidden", true);
      const quantityInputs = bundle.querySelectorAll("[data-bulk-add]");

      quantityInputs.forEach((quantityInput) => {
        quantityInput.removeAttribute("data-quantity");
        if (quantityInput.type === "checkbox") {
          quantityInput.checked = false;
        } else {
          const currentQuantityElement = quantityInput.parentElement?.querySelector("[data-bundle-item-quantity]");
          if (currentQuantityElement) {
            currentQuantityElement.textContent = "0";
          }
        }
      });

      if (+bundle.dataset.bundleVariant === selectedVariantId) {
        bundle.removeAttribute("hidden");
      }
    });
  }
}

customElements.define("product-bundle-options", ProductBundleOptions);
